import { useEffect } from 'react'
import { app } from '@/lib/api/client'
import { isPlaceholderAvatar } from '@/lib/avatar/startingAvatar'
import { useUserStore, type UserProfile } from '@/stores/userStore'

/**
 * Server-side profile sync (world-bff `GET/PATCH /api/me`).
 *
 * The server is the source of truth when it has a stored profile; the persisted
 * user store (localStorage) is the cache and the fallback. A world whose BFF has
 * no profile store yet answers `profilePersisted: false` (or fails), and then
 * nothing changes here: the local copy keeps working exactly as before.
 */

/** The stored overrides as the BFF returns them (null = not set). */
export interface StoredProfile {
  displayName: string | null
  avatarUrl: string | null
  backstory: string | null
  isPublic: boolean
  prefs?: Record<string, unknown>
  updatedAt?: string | null
}

export interface MeProfileResponse {
  profilePersisted?: boolean
  profile?: StoredProfile | null
}

export interface ProfileSyncPlan {
  /** Fields to apply to the local store from the server. */
  apply: Partial<UserProfile> | null
  /** Fields to push to the server (seed it with the local choice). */
  push: { avatarUrl: string } | null
}

/** A portrait the server can store: a real choice, never a placeholder or blob. */
export function isShareableAvatar(url: string | null | undefined): url is string {
  return !!url && !isPlaceholderAvatar(url) && (url.startsWith('https://') || (url.startsWith('/') && !url.startsWith('//')))
}

/**
 * Decide how to reconcile the local user with the server's stored profile.
 * - Server unavailable: do nothing (local stays authoritative).
 * - Server has a portrait: it wins locally.
 * - Server has none but the player has a real local portrait (the starting
 *   headshot default, a picker choice, or an upload): push it up once.
 */
export function planProfileSync(local: Pick<UserProfile, 'avatarUrl' | 'backstory' | 'isPublic' | 'displayName'>, me: MeProfileResponse | null): ProfileSyncPlan {
  if (!me || me.profilePersisted !== true || !me.profile) {
    // Persisted store answered but no row yet → seed with the local portrait.
    if (me?.profilePersisted === true && isShareableAvatar(local.avatarUrl)) {
      return { apply: null, push: { avatarUrl: local.avatarUrl } }
    }
    return { apply: null, push: null }
  }
  const s = me.profile
  const apply: Partial<UserProfile> = {}
  if (isShareableAvatar(s.avatarUrl)) {
    if (s.avatarUrl !== local.avatarUrl) apply.avatarUrl = s.avatarUrl
  }
  if (s.displayName && s.displayName !== local.displayName) apply.displayName = s.displayName
  if (s.backstory !== null && s.backstory !== local.backstory) apply.backstory = s.backstory
  if (s.updatedAt && s.isPublic !== local.isPublic) apply.isPublic = s.isPublic
  const push = !isShareableAvatar(s.avatarUrl) && isShareableAvatar(local.avatarUrl) ? { avatarUrl: local.avatarUrl } : null
  return { apply: Object.keys(apply).length ? apply : null, push }
}

/**
 * Save profile fields to the server. Returns the stored profile, or null when
 * the server cannot store it (the caller keeps its local copy either way).
 * A local `blob:` preview is never sent.
 */
export async function saveServerProfile(patch: Partial<Pick<UserProfile, 'displayName' | 'backstory' | 'isPublic' | 'avatarUrl'>> & { prefs?: Record<string, unknown> }): Promise<StoredProfile | null> {
  const body: Record<string, unknown> = { ...patch }
  if ('avatarUrl' in body && body.avatarUrl !== null && !isShareableAvatar(body.avatarUrl as string)) delete body.avatarUrl
  try {
    const res = await app.patch<MeProfileResponse>('/me', body)
    return res?.profilePersisted ? res.profile ?? null : null
  } catch {
    return null
  }
}

const synced = new Set<string>()

/** Pull the stored profile once per signed-in account per page load. */
export async function syncServerProfile(): Promise<void> {
  const { user, status } = useUserStore.getState()
  if (status !== 'authenticated' || !user || synced.has(user.id)) return
  synced.add(user.id)
  let me: MeProfileResponse | null = null
  try {
    me = await app.get<MeProfileResponse>('/me')
  } catch {
    return
  }
  const current = useUserStore.getState().user
  if (!current || current.id !== user.id) return
  const plan = planProfileSync(current, me)
  if (plan.apply) useUserStore.getState().updateProfile(plan.apply)
  if (plan.push) await saveServerProfile(plan.push)
}

/** Mount once in the app shell: syncs on sign-in and on session restore. */
export function useServerProfileSync(): void {
  const userId = useUserStore((s) => (s.status === 'authenticated' ? s.user?.id ?? null : null))
  useEffect(() => {
    if (userId) void syncServerProfile()
  }, [userId])
}

/** Test hook. */
export function __resetProfileSync(): void {
  synced.clear()
}
